import { DropdownMenu } from "@/components/molecules/DropdownMenu/DropdownMenu";
import { parseHexWithAlpha } from "@/layers/utility/color";
import {
  GenericColorPicker,
  type GenericColorPickerSavedColorsState,
} from "@/overlay/components/GenericColorPicker/GenericColorPicker";
import { SavedColorsCloseConfirmationModal } from "@/overlay/components/GenericColorPicker/SavedColorsCloseConfirmationModal";
import {
  type SavedColorsCloseConfirmationConfig,
  requestSavedColorsClose,
} from "@/overlay/components/GenericColorPicker/requestSavedColorsClose";
import { FormField, type FormFieldProps } from "@/overlay/components/form-field";
import { t } from "@lingui/core/macro";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type IColor, useColor } from "react-color-palette";

/**
 * Props for {@link ColorPickerInput}.
 */
export type ColorPickerInputProps = {
  /** Label displayed by the surrounding form field. */
  label: string;
  /** Accessible name used when the visual label is omitted or not specific enough. */
  ariaLabel?: string;
  /** Current color value used to initialize/update the picker. */
  value: string;
  /**
   * Commit callback fired with the normalized selected color.
   * 8-digit hex values are converted to `rgba(...)`.
   */
  change: (color: string) => void;
  /** Optional settings forwarded to the wrapping {@link FormField}. */
  formFieldOptions?: Partial<FormFieldProps>;
  /** Renders the dropdown inside a portal when set to `true`. */
  withinPortal?: boolean;
  /** Shows checkerboard pattern under translucent colors when set to `true`. */
  showTransparencyGrid?: boolean;
  /**
   * Enables live apply while the user changes the color.
   * When `false`/`undefined`, color is applied on dropdown close.
   */
  commitOnChangeComplete?: boolean;
  /** Debounce time in milliseconds for live apply mode. Defaults to `180`. */
  commitDebounceMs?: number;
  /** Closes the picker immediately after selecting a saved color. */
  closeOnSavedColorSelect?: boolean;
};

/**
 * Color input with a dropdown picker.
 *
 * Default behavior commits the color when the dropdown closes.
 * If `commitOnChangeComplete` is enabled, commits happen during interaction (debounced).
 */
export function ColorPickerInput(props: ColorPickerInputProps) {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [color, setColor] = useColor(props.value || "");
  const [savedColorsState, setSavedColorsState] = useState<GenericColorPickerSavedColorsState | null>(null);
  const [closeConfirmation, setCloseConfirmation] = useState<SavedColorsCloseConfirmationConfig | null>(null);
  const alpha = color.rgb.a ?? 1;
  const showCheckerboard = Boolean(props.showTransparencyGrid) && alpha < 1;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const lastCommittedColorRef = useRef<string | null>(null);
  const latestHexRef = useRef(color.hex);

  const commitColor = useCallback(
    (hexColor: string) => {
      const parsedColor = parseHexWithAlpha(hexColor);
      props.change(parsedColor);
      lastCommittedColorRef.current = parsedColor;
    },
    [props.change],
  );

  const commitDebounceMs = props.commitDebounceMs ?? 180;
  const debouncedCommitColor = useMemo(
    () => debounce((hexColor: string) => commitColor(hexColor), commitDebounceMs),
    [commitColor, commitDebounceMs],
  );

  useEffect(() => {
    latestHexRef.current = color.hex;
  }, [color.hex]);

  useEffect(() => () => debouncedCommitColor.cancel(), [debouncedCommitColor]);

  const handleColorChange = useCallback(
    (nextColor: IColor) => {
      latestHexRef.current = nextColor.hex;
      setColor(nextColor);
      if (props.commitOnChangeComplete) {
        debouncedCommitColor(nextColor.hex);
      }
    },
    [props.commitOnChangeComplete, debouncedCommitColor, setColor],
  );

  const handleClose = () => {
    requestSavedColorsClose({
      savedColorsState,
      onConfirmedClose: () => {
        if (props.commitOnChangeComplete) {
          debouncedCommitColor.flush();
          const parsedColor = parseHexWithAlpha(latestHexRef.current);
          if (lastCommittedColorRef.current !== parsedColor) {
            props.change(parsedColor);
            lastCommittedColorRef.current = parsedColor;
          }
        } else {
          props.change(parseHexWithAlpha(color.hex));
        }
        setCloseConfirmation(null);
        setDisplayColorPicker(false);
      },
      confirmUnsavedChanges: (config) => {
        setCloseConfirmation(config);
      },
    });
  };

  const handleClick = () => setDisplayColorPicker((prev) => !prev);

  return (
    <div ref={wrapperRef}>
      <FormField {...props.formFieldOptions} label={props.label}>
        <button
          type="button"
          className={`color-picker__swatch${showCheckerboard ? " color-picker__swatch--checkerboard" : ""}`}
          onClick={handleClick}
          aria-label={props.ariaLabel ?? props.label}
          aria-haspopup="dialog"
          aria-expanded={displayColorPicker}
          data-testid="color-picker-input"
          ref={anchorRef}
        >
          <div className="color-picker__color" style={{ background: color.hex }} />
        </button>
      </FormField>
      <DropdownMenu
        isOpen={displayColorPicker}
        withinPortal={props.withinPortal}
        onClose={() => handleClose()}
        anchorRef={wrapperRef}
      >
        {closeConfirmation ? (
          <SavedColorsCloseConfirmationModal isOpen={true} {...closeConfirmation} />
        ) : (
          <GenericColorPicker
            color={color}
            onColorChange={handleColorChange}
            closeOnSavedColorSelect={props.closeOnSavedColorSelect}
            onRequestClose={handleClose}
            onSavedColorsStateChange={setSavedColorsState}
          />
        )}
      </DropdownMenu>
    </div>
  );
}
